// Generates a downloadable PDF of a completed call: metadata, captured data,
// and the full turn-by-turn transcript. Client-side only — nothing sent to a
// server, works on any call the user can already load in the detail dialog.
//
// The `call` argument matches the shape returned by GET /api/ai-voice/calls/:id
// (transcript already parsed into an array, extractedData already parsed).

import { jsPDF } from "jspdf";

// ── Layout constants (points) ────────────────────────────────────────────────
const PAGE = { width: 595, height: 842 };
const MARGIN = { top: 54, bottom: 54, left: 54, right: 54 };
const LINE = 14;
const CONTENT_WIDTH = PAGE.width - MARGIN.left - MARGIN.right;

const COLORS = {
  text: [15, 23, 42],
  muted: [100, 116, 139],
  faint: [148, 163, 184],
  accent: [79, 70, 229],
  aiBg: [79, 70, 229],
  aiText: [255, 255, 255],
  targetBg: [241, 245, 249],
  targetText: [15, 23, 42],
  border: [229, 231, 235],
  success: [22, 101, 52],
  successBg: [220, 252, 231],
  failure: [153, 27, 27],
  failureBg: [254, 226, 226],
};

// ── Cursor helpers ──────────────────────────────────────────────────────────
function newCursor() {
  return { y: MARGIN.top, page: 1 };
}

function ensureSpace(doc, cursor, needed) {
  if (cursor.y + needed > PAGE.height - MARGIN.bottom) {
    drawFooter(doc, cursor);
    doc.addPage();
    cursor.page += 1;
    cursor.y = MARGIN.top;
    return true;
  }
  return false;
}

function drawFooter(doc, cursor) {
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.faint);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Vishtrap · Page ${cursor.page}`,
    PAGE.width / 2,
    PAGE.height - 20,
    { align: "center" }
  );
}

// ── Building blocks ─────────────────────────────────────────────────────────
function drawHeader(doc, cursor, call) {
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.text);
  doc.text("Vishtrap Call Transcript", MARGIN.left, cursor.y);
  cursor.y += 22;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.muted);
  const target = String(call.phoneNumber || "—");
  const idText = `Call #${call.id}`;
  doc.text(`${target}    ·    ${idText}`, MARGIN.left, cursor.y);
  cursor.y += 6;

  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(MARGIN.left, cursor.y + 4, PAGE.width - MARGIN.right, cursor.y + 4);
  cursor.y += 20;
}

function drawSectionTitle(doc, cursor, title) {
  ensureSpace(doc, cursor, 24);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.muted);
  doc.text(title.toUpperCase(), MARGIN.left, cursor.y);
  cursor.y += 6;
  doc.setDrawColor(...COLORS.border);
  doc.line(MARGIN.left, cursor.y, PAGE.width - MARGIN.right, cursor.y);
  cursor.y += 10;
}

function drawKeyValue(doc, cursor, key, value) {
  ensureSpace(doc, cursor, LINE * 2);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.muted);
  doc.text(key, MARGIN.left, cursor.y);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.text);
  const wrapped = doc.splitTextToSize(String(value || "—"), CONTENT_WIDTH - 120);
  doc.text(wrapped, MARGIN.left + 120, cursor.y);
  cursor.y += Math.max(LINE, wrapped.length * LINE) + 2;
}

function drawParagraph(doc, cursor, text) {
  const wrapped = doc.splitTextToSize(String(text), CONTENT_WIDTH);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.text);
  for (const line of wrapped) {
    ensureSpace(doc, cursor, LINE);
    doc.text(line, MARGIN.left, cursor.y);
    cursor.y += LINE;
  }
  cursor.y += 4;
}

function drawOutcomeBadge(doc, cursor, outcome) {
  if (!outcome) return;
  const key = String(outcome).toLowerCase();
  const style =
    key === "success" ? { bg: COLORS.successBg, text: COLORS.success, label: "Success" } :
    key === "failure" ? { bg: COLORS.failureBg, text: COLORS.failure, label: "Failure" } :
    { bg: COLORS.targetBg, text: COLORS.muted, label: "Unknown" };
  const badgeW = 70, badgeH = 18;
  ensureSpace(doc, cursor, badgeH + 6);
  doc.setFillColor(...style.bg);
  doc.roundedRect(MARGIN.left, cursor.y - 12, badgeW, badgeH, 3, 3, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...style.text);
  doc.text(style.label, MARGIN.left + badgeW / 2, cursor.y, { align: "center" });
  cursor.y += badgeH + 4;
}

// ── Transcript bubble ───────────────────────────────────────────────────────
function drawTranscriptTurn(doc, cursor, turn) {
  const isAi = turn.role === "ai";
  const label = isAi ? "AI AGENT" : "TARGET";
  const bg = isAi ? COLORS.aiBg : COLORS.targetBg;
  const fg = isAi ? COLORS.aiText : COLORS.targetText;

  const bubbleMaxWidth = CONTENT_WIDTH * 0.78;
  const bubblePadding = 8;
  const textWidth = bubbleMaxWidth - bubblePadding * 2;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const wrapped = doc.splitTextToSize(String(turn.content || ""), textWidth);
  const timeText = turn.time ? new Date(turn.time).toLocaleTimeString() : "";

  const bubbleHeight = 16 + wrapped.length * 12 + (timeText ? 10 : 0) + bubblePadding;
  ensureSpace(doc, cursor, bubbleHeight + 8);

  const bubbleX = isAi
    ? PAGE.width - MARGIN.right - bubbleMaxWidth
    : MARGIN.left;

  doc.setFillColor(...bg);
  doc.roundedRect(bubbleX, cursor.y, bubbleMaxWidth, bubbleHeight, 4, 4, "F");

  // role label
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...fg);
  doc.text(label, bubbleX + bubblePadding, cursor.y + bubblePadding + 4);

  // message
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...fg);
  let ty = cursor.y + bubblePadding + 16;
  for (const line of wrapped) {
    doc.text(line, bubbleX + bubblePadding, ty);
    ty += 12;
  }

  // timestamp
  if (timeText) {
    doc.setFontSize(7);
    doc.text(timeText, bubbleX + bubbleMaxWidth - bubblePadding, ty, { align: "right" });
  }

  cursor.y += bubbleHeight + 6;
}

// ── Public entry point ──────────────────────────────────────────────────────
export function generateCallPdf(call) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const cursor = newCursor();

  drawHeader(doc, cursor, call);

  // Metadata
  drawSectionTitle(doc, cursor, "Metadata");
  drawKeyValue(doc, cursor, "Target", call.phoneNumber);
  drawKeyValue(doc, cursor, "Voice", call.voiceName);
  drawKeyValue(doc, cursor, "Status", call.callStatus);
  drawKeyValue(doc, cursor, "Started", call.startTime ? new Date(call.startTime).toLocaleString() : "—");
  if (call.endTime) drawKeyValue(doc, cursor, "Ended", new Date(call.endTime).toLocaleString());
  if (call.fromNumber) drawKeyValue(doc, cursor, "Caller ID", call.fromNumber);
  cursor.y += 4;

  // Captured Data
  const ex = call.extractedData || {};
  const isRich = "collected" in ex || "criteria" in ex || "regex_matches" in ex || "outcome" in ex || "summary" in ex;
  const hasAnyData = Object.keys(ex).length > 0;

  if (hasAnyData) {
    drawSectionTitle(doc, cursor, "Captured Data");
    if (isRich) {
      if (ex.outcome) drawOutcomeBadge(doc, cursor, ex.outcome);
      if (ex.summary) {
        drawKeyValue(doc, cursor, "Call Summary", "");
        drawParagraph(doc, cursor, ex.summary);
      }
      const collected = ex.collected || {};
      if (Object.keys(collected).length > 0) {
        for (const [k, entry] of Object.entries(collected)) {
          const value = entry && typeof entry === "object" && "value" in entry ? entry.value : entry;
          const rationale = entry && typeof entry === "object" ? entry.rationale : "";
          const displayValue = typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
          drawKeyValue(doc, cursor, formatKey(k), displayValue);
          if (rationale) drawParagraph(doc, cursor, `  ${rationale}`);
        }
      }
      const criteria = ex.criteria || {};
      if (Object.keys(criteria).length > 0) {
        cursor.y += 6;
        drawSectionTitle(doc, cursor, "Evaluation Criteria");
        for (const [k, entry] of Object.entries(criteria)) {
          const result = entry && typeof entry === "object" && "value" in entry ? entry.value : entry;
          const rationale = entry && typeof entry === "object" ? entry.rationale : "";
          drawKeyValue(doc, cursor, formatKey(k), formatCriteriaResult(result));
          if (rationale) drawParagraph(doc, cursor, `  ${rationale}`);
        }
      }
      const regex = ex.regex_matches || {};
      if (Object.keys(regex).length > 0) {
        cursor.y += 6;
        drawSectionTitle(doc, cursor, "Pattern Matches");
        for (const [k, v] of Object.entries(regex)) {
          drawKeyValue(doc, cursor, formatKey(k), String(v));
        }
      }
    } else {
      // legacy flat format
      for (const [k, v] of Object.entries(ex)) {
        drawKeyValue(doc, cursor, formatKey(k), String(v));
      }
    }
    cursor.y += 4;
  }

  // Transcript
  drawSectionTitle(doc, cursor, "Conversation Transcript");
  const turns = Array.isArray(call.transcript) ? call.transcript : [];
  if (turns.length === 0) {
    drawParagraph(doc, cursor, "No transcript captured for this call.");
  } else {
    for (const turn of turns) drawTranscriptTurn(doc, cursor, turn);
  }

  drawFooter(doc, cursor);

  const filename = buildFilename(call);
  doc.save(filename);
}

// ── Small utilities ─────────────────────────────────────────────────────────
function formatKey(k) {
  return String(k)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCriteriaResult(r) {
  const key = String(r).toLowerCase();
  if (key === "success") return "Pass";
  if (key === "failure") return "Fail";
  return "Unknown";
}

function buildFilename(call) {
  const date = call.startTime ? new Date(call.startTime).toISOString().slice(0, 10) : "call";
  const target = String(call.phoneNumber || "unknown")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `vishtrap-call-${call.id}-${target}-${date}.pdf`;
}
